<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent max-width=700 content-class="round-2xl")
  v-card
    div.py-6.px-10
      .text-xl.font-semibold.mb-2 Share Your Quotation Request
      v-card.rounded-lg.pa-4(outlined)
        .text-sm.mb-3 {{ headerText }}
        .text-sm.mb-3 {{ bodyText.text1 }} {{ bodyText.text2 }}&nbsp;
          span.blue--text {{ quotationLink }}
        .text-sm.mb-3 {{ bodyText.text3 }}
        .text-sm.mb-3.px-2
          div {{ bodyText.text4 }}
          div {{ bodyText.text5 }}
          div {{ bodyText.text6 }}
          div {{ bodyText.text7 }}
        .text-sm.mb-5
          span(v-if="respondDate") {{ bodyText.text8 }}
          span(v-if="enquiryText") &nbsp;{{ enquiryText }}
        .text-sm {{ bodyText.text10 }}
        .text-sm {{ bodyText.text11 }}
      .d-flex.align-center.justify-end.my-4
        v-btn.button-sm.mr-1(depressed dark :loading="isLoading.copyText" @click="handleCopyText")
          v-icon.mr-1(size="15") $yzi-copy-03
          .text-sm Copy text
        v-tooltip(top color="black" content-class="black tooltip-top-arrow")
          template(#activator="{ on, attrs }")
            v-btn.button-sm.mr-1(depressed dark v-on="on" :loading="isLoading.downloadPdf" @click="handleDownloadPdf")
              v-icon.mr-1(size="15") $yzi-download-02
              .text-sm Download PDF
          div(style="max-width: 226px")
            .text-sm Download your quotation request as a PDF document.
        v-tooltip(top color="black" content-class="black tooltip-top-arrow")
          template(#activator="{ on, attrs }")
            v-btn.button-sm(depressed dark v-on="on" :loading="isLoading.sendWhatsapp" :disabled="!hasSupplierPhone" @click="handleSendWhatsapp")
              v-icon.mr-1(size="15") mdi-whatsapp
              .text-sm Send Whatsapp
          div(style="max-width: 226px")
            .text-sm Redirect to WhatsApp to share your quotation request with your supplier.
      .text-md.font-semibold.mb-1 Quotation link
      v-text-field.rounded-lg(v-model="quotationLink" dense outlined readonly)
        template(v-slot:append)
          v-tooltip(top color="black" content-class="black tooltip-top-arrow")
            template(#activator="{ on, attrs }")
              v-btn.mb-2.button-sm(outlined v-on="on" :loading="isLoading.copyLink" @click="handleCopyLink")
                v-icon.mr-1(size="15") $yzi-link-03
                .text-sm Copy link
            div(style="max-width: 226px")
              .text-sm Copy a link to your quotation request to share it easily.
    v-divider
    v-card-actions.px-6
      v-spacer
      v-btn.button-md.neutral700--text.px-0.underline(text @click="closeDialog") {{ $t('close') }}
  div(ref="copy")
  </template>
    
<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'

export default {
  props: {
    open: {
      type: Boolean,
    },
    parentData: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      isLoading: {
        copyText: false,
        downloadPdf: false,
        sendWhatsapp: false,
        copyLink: false
      }
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    quotationLink () {
      let baseUrl = process.env.VUE_APP_BASE_DOMAIN
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = `http://${window.location.host}`
      }
      return `${baseUrl}/quotation/${this.company.slug}/${this.parentData?.pid}`
    },
    respondDate () {
      if (this.parentData.record_date) {
        return this.$options.filters.convertToDateOnly(DateTime.fromISO(this.parentData.record_date).plus({ days: 7 }))
      }
      return null
    },
    hasCompanyPhone () {
      return this.company.phone !== null
    },
    hasCompanyEmail () {
      return this.company.email !== null
    },
    hasCompanyPhoneOrEmail () {
      return this.hasCompanyPhone || this.hasCompanyEmail
    },
    enquiryText () {
      if (this.hasCompanyPhoneOrEmail) {
        const baseText = 'For any queries,'
        const phoneText = `contact us at +${this.company.phone}`
        const emailText = `email us at ${this.company.email}`
        if (this.hasCompanyPhone && this.hasCompanyEmail) {
          return `${baseText} ${phoneText} or ${emailText}.`
        } else if (this.hasCompanyPhone) {
          return `${baseText} ${phoneText}.`
        } else if (this.hasCompanyEmail) {
          return `${baseText} ${emailText}.`
        }
      }
      return null
    },
    headerText () {
      return `Dear ${this.parentData?.supplier?.value?.toUpperCase()},`
    },
    bodyText () {
      return {
        text1: 'We are seeking a quotation for items listed in the attached document.',
        text2: 'Please find the details here:',
        text3: 'Kindly provide:',
        text4: '1. Pricing details.',
        text5: '2. Estimated delivery time.',
        text6: '3. Any bulk order discounts.',
        text7: '4. Payment terms.',
        text8: `Please respond by ${this.respondDate}.`,
        text9: this.enquiryText,
        text10: 'Thank you,',
        text11: `${this.company.name.toUpperCase()}`
      }
    },
    computedText () {
      let mainText = 
      `
      ${this.headerText}
      
      ${this.bodyText.text1} ${this.bodyText.text2} ${this.quotationLink}

      ${this.bodyText.text3}

      ${this.bodyText.text4}
      ${this.bodyText.text5}
      ${this.bodyText.text6}
      ${this.bodyText.text7}

      `

      if (this.respondDate) {
        mainText += `${this.bodyText.text8} `
      }

      if (this.enquiryText) {
        mainText += `${this.enquiryText}`
      }

      return `${mainText}

      ${this.bodyText.text10}
      ${this.bodyText.text11}`
    },
    hasSupplierPhone () {
      return this.parentData?.supplier?.extra_data?.phone !== null
    },
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
    handleCopyText () {
      this.isLoading.copyText = true
      let container = this.$refs.copy
      this.$copyText(this.computedText, container)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.content_copied_to_clipboard')])
        })
      this.isLoading.copyText = false
    },
    encodeMessage (message) {
      let encodedMessage = message.split(/\n/g).join('%0d%0a')
      encodedMessage = this.$options.filters.encodeSymbols(encodedMessage)
      return encodedMessage
    },
    handleSendWhatsapp () {
      this.isLoading.sendWhatsapp = true
      const encodedMessage = this.encodeMessage(this.computedText)
      const phone = this.parentData?.supplier?.extra_data?.phone
      window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${encodedMessage}&app_absent=1`)
      setTimeout(() => {
        window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${encodedMessage}&app_absent=1`)
      }, 200)
      this.isLoading.sendWhatsapp = false
    },
    async handleDownloadPdf () {
      this.isLoading.downloadPdf = true
      // Get key first
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoading.downloadPdf = false
        return
      }
      // Open download url
      let baseUrl
      if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      } else {
        baseUrl = 'https://export-test.yezza.io'
      }
      // Url setup
      let url = `${baseUrl}/purchases/${this.company.slug}/quotations/${this.parentData.pid}/preview_document`
      url = new URL(url)
      // Set params
      url.searchParams.set('key', key)
      url.searchParams.set('pid', true)
      // Download
      window.open(url, '_blank')
      this.isLoading.downloadPdf = false
    },
    handleCopyLink () {
      this.isLoading.copyLink = true
      let container = this.$refs.copy
      this.$copyText(this.quotationLink, container)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.link_copied_to_clipboard')])
        })
      this.isLoading.copyLink = false
    },
  },
}
  </script>
  
  <style scoped>
  </style>